import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "../Food/RecipeList.css";
import "./BabyTracker.css";
import {
  bottleMilkTypeForEvent,
  diaperTypes,
  fetchBabyFeedingHistory,
  eventCategoryLabels,
  formatDurationFromMinutes,
  formatFeedingAmount,
  getNowLocalDateTime,
  normalizeFeedingType,
} from "./BabyTrackerShared";
import BabyTrackerLoading from "./BabyTrackerLoading";

const formatDateInput = (date) => date.toISOString().slice(0, 10);

const todayDateInput = () => getNowLocalDateTime().slice(0, 10);

const formatOunces = (value) => {
  const numberValue = Number(value || 0);
  if (Number.isNaN(numberValue)) return "0 oz";
  return `${Number.isInteger(numberValue) ? numberValue : numberValue.toFixed(2).replace(/0+$/, "").replace(/\.$/, "")} oz`;
};

const addDays = (dateValue, dayOffset) => {
  const date = new Date(`${dateValue}T12:00:00`);
  date.setDate(date.getDate() + dayOffset);
  return formatDateInput(date);
};

const eventTitle = (entry) => {
  if (entry.eventCategory === "diaper") return entry.eventType;
  if (entry.eventCategory === "pumping") return "Pumping";
  return normalizeFeedingType(entry.eventType);
};

const eventDetail = (entry) => {
  if (entry.eventCategory === "diaper") return "Diaper change";
  if (entry.eventCategory === "pumping") return "Pumped";
  if (normalizeFeedingType(entry.eventType) === "Bottle") {
    return bottleMilkTypeForEvent(entry);
  }
  return entry.feedingSide ? `Started ${entry.feedingSide}` : "Breastfed";
};

const BabyTrackerFeedingHistory = () => {
  const [selectedDate, setSelectedDate] = useState(todayDateInput());
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    setLoading(true);
    setLoadError("");
    fetchBabyFeedingHistory(selectedDate)
      .then((data) => {
        setHistory(data);
        setLoading(false);
      })
      .catch((error) => {
        setHistory(null);
        setLoadError(error.message || "Unable to load feeding history.");
        setLoading(false);
      });
  }, [selectedDate]);

  const totalFeedings = useMemo(() => (
    Number(history?.bottleCount || 0) + Number(history?.breastCount || 0)
  ), [history]);
  const diaperCounts = history?.diaperCounts || {};

  return (
    <div className="BabyTrackerPage">
      <div className="searchBarDiv formViewWrapper BabyTrackerIntro">
        <div>
          <h2 className="BabyTrackerTitle">Daily history</h2>
          <p className="BabyTrackerSubtitle">
            Review daily diapers, feeding, breastfeeding, and pumping totals.
          </p>
        </div>
        <Link className="summaryButton BabyTrackerLinkButton" to="/baby-tracker">
          Home
        </Link>
      </div>

      <div className="shoppingListDiv BabyTrackerPanel BabyTrackerSinglePanel">
        <div className="BabyTrackerHistoryDateHeader">
          <button
            className="summaryButton BabyTrackerDateStepButton"
            onClick={() => setSelectedDate((currentDate) => addDays(currentDate, -1))}
            type="button"
          >
            Previous
          </button>
          <div className="inputDiv BabyTrackerDateInputWrap">
            <label>Date</label>
            <input
              className="inputBox addInput"
              name="feedingHistoryDate"
              onChange={(event) => setSelectedDate(event.target.value || todayDateInput())}
              type="date"
              value={selectedDate}
            />
          </div>
          <button
            className="summaryButton BabyTrackerDateStepButton"
            onClick={() => setSelectedDate((currentDate) => addDays(currentDate, 1))}
            type="button"
          >
            Next
          </button>
        </div>
        {loadError ? <p className="BabyTrackerError">{loadError}</p> : null}
        {loading ? <BabyTrackerLoading label="Loading feeding history" /> : null}
      </div>

      {!loading && history ? (
        <>
          <div className="shoppingListDiv BabyTrackerPanel BabyTrackerSinglePanel">
            <h3>Daily totals</h3>
            <div className="BabyTrackerMetricGrid">
              <div className="BabyTrackerMetric">
                <span>Diapers</span>
                <strong>{history.diaperTotal || 0}</strong>
              </div>
              <div className="BabyTrackerMetric">
                <span>Bottle eaten</span>
                <strong>{formatOunces(history.bottleOunces)}</strong>
              </div>
              <div className="BabyTrackerMetric">
                <span>Breastfed time</span>
                <strong>{formatDurationFromMinutes(history.breastMinutes) || "0 min"}</strong>
              </div>
              <div className="BabyTrackerMetric">
                <span>Pumped</span>
                <strong>{formatOunces(history.pumpingOunces)}</strong>
              </div>
              <div className="BabyTrackerMetric">
                <span>Feeds logged</span>
                <strong>{totalFeedings}</strong>
              </div>
            </div>
          </div>

          <div className="shoppingListDiv BabyTrackerPanel BabyTrackerSinglePanel">
            <h3>Diaper counts</h3>
            <div className="BabyTrackerMetricGrid">
              {diaperTypes.map((diaperType) => (
                <div className="BabyTrackerMetric" key={diaperType}>
                  <span>{diaperType}</span>
                  <strong>{diaperCounts[diaperType] || 0}</strong>
                </div>
              ))}
            </div>
          </div>

          <div className="shoppingListDiv BabyTrackerPanel BabyTrackerSinglePanel">
            <h3>Entries</h3>
            {history.events?.length ? (
              history.events.map((entry) => (
                <div className="BabyTrackerHistoryRow" key={entry.id}>
                  <div>
                    <strong>{eventTitle(entry)}</strong>
                    <div>{eventCategoryLabels[entry.eventCategory]}</div>
                    <div>{entry.occurredAt}</div>
                    {entry.endedAt ? <div>Ended {entry.endedAt}</div> : null}
                  </div>
                  <span>
                    {[formatFeedingAmount(entry), eventDetail(entry)].filter(Boolean).join(" - ")}
                  </span>
                </div>
              ))
            ) : (
              <p>No diaper, feeding, or pumping entries for this day.</p>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
};

export default BabyTrackerFeedingHistory;
